/*
 * Every user-facing string the Feed Medic report renders client-side, in one
 * place, so a language pass is a single-file edit rather than a hunt across the
 * component tree.
 *
 * What is NOT here: the diagnostic sentences themselves — every finding
 * headline, detail, and bucket summary is generated server-side by
 * code/classes/Dlvr/Diagnostic/Checks/*.php and persisted per run. Rewording
 * those means editing the PHP, and because they're stored, the change is not
 * retroactive: runs already on disk keep their original wording for the full
 * 30-day retention window.
 *
 * Interpolated strings are exported as functions so the format lives here too
 * rather than being assembled at the call site.
 */

export const panel = {
    title:          'Feed Medic',
    runIdle:        'Run Medic',
    runBusy:        'Medic Running…',
    shareIdle:      'Copy Share Link',
    shareCopied:    'Link Copied!',
} as const;

/*
 * ONE severity vocabulary, shared by the run verdict and the section badges.
 *
 * Previously the verdict said Healthy/Warnings/Critical/Error while sections
 * said OK/Warnings/Issues/Pending/Skipped/Error — two ladders whose words
 * didn't line up, so a run labelled "Error" contained a section labelled
 * "Issues" with nothing to tell the reader those describe the same thing.
 */
export const states = {
    passed:   'Passed',
    warnings: 'Warnings',
    issues:   'Issues',
    failed:   'Failed',
    pending:  'Pending',
    running:  'Checking',
    skipped:  'Skipped',
} as const;

export const verdicts = {
    healthy:  {label: states.passed,   message: 'No problems found.'},
    warnings: {label: states.warnings, message: 'Worth a look, but nothing here stops this feed posting.'},
    critical: {label: states.issues,   message: 'Found problems that can stop this feed posting.'},
    error:    {label: states.failed,   message: "The diagnostic couldn't finish — see below."},
    /*
     * The last two are run states, not verdicts — a run only gets a verdict when
     * it closes. They are the header's fallback so it always states something,
     * and are shown only when no progress bar is carrying that job instead.
     *
     * `queued` is deliberately worded as not-yet-started rather than in-progress:
     * the worker writes its first progress the moment it picks a run up
     * (FeedMedicRun::execute), so a queued run with no progress has not begun,
     * and saying "Checking this feed…" would claim work that isn't happening.
     */
    pending:  {label: states.running,  message: 'Checking this feed…'},
    queued:   {label: states.pending,  message: 'Waiting to start…'},
} as const;

/*
 * Named for what each stage does, in the order the pipeline runs them: fetch the
 * feed, read the pages it links to, post to the destinations, with settings
 * gating all of it. Reading as a sequence is what makes a skipped section
 * self-explanatory — an earlier stage failed, so the later ones never ran.
 *
 * 'Feed Accessibility' and 'Content Quality' were both actively misleading:
 * the first collides with the accessibility/a11y sense of the word when it
 * actually means "can we fetch this file", and the second reads as a judgement
 * on the user's writing when every finding under it is about og:/twitter: tags —
 * which is to say link previews.
 */
export const bucketTitles = {
    accessibility: 'Reading the feed',
    content:       'Link previews',
    social:        'Posting to your accounts',
    settings:      'Feed settings',
} as const;

/** Derived section state (see severity.ts) → the same shared labels. */
export const bucketStates = {
    pending:  states.pending,
    running:  states.running,
    skipped:  states.skipped,
    ok:       states.passed,
    warning:  states.warnings,
    critical: states.issues,
} as const;

export const header = {
    ranLabel: 'Ran',
} as const;

/*
 * Shown before the first run. Leads with what the reader gets rather than
 * announcing an absence the blank panel already conveys.
 *
 * Note the body paraphrases the four stages rather than quoting bucketTitles, so
 * renaming a stage will not update this sentence. That is the cost of reading as
 * prose instead of a list; if the stage names change, this needs changing too.
 */
export const emptyState = {
    heading: "Find out why this feed isn't posting",
    body:    'Medic reads this feed the way the social platforms do, then reports back on whether it '
        + 'can be read, how link previews will look, whether posts are reaching your accounts, and '
        + 'what your settings allow.',
    timing:  'Takes a few seconds.',
} as const;

export const progressPhases = {
    init:          'Starting',
    accessibility: 'Reading the feed',
    content:       'Checking link previews',
    social:        'Reviewing recent posting',
    settings:      'Reviewing feed settings',
} as const;

/** Shown when `progress.phase` is unrecognised and `progress.msg` is empty. */
export const progressFallback = 'Running…';

/*
 * Disclosure labels now name what is inside them. The report previously nested
 * "Show details" → "Raw details" → "Response headers" three deep, where the
 * outer two labels described the act of expanding rather than the content, so a
 * reader had to open both to find out whether either was worth opening.
 */
export const disclosure = {
    rawImages:   'Image details',
    headers:     'All response headers',
    filteredOut: 'Examples of filtered-out items',
    feedResponse: 'Feed response',
    sampledItems: 'Sampled items',
    perDestination: 'Posting by destination',
    filterDefinition: 'Filter definition',
} as const;

/**
 * Count shown in a section's header band, in place of restating the state word
 * the band's colour already carries. Per-section counts appeared nowhere in the
 * report before this.
 *
 * Reports the count of whichever severity set the section's state, so a section
 * reading as an issue is quantified by its issues rather than by its total rows.
 */
export const sectionTally = (
    state: 'ok' | 'warning' | 'critical',
    counts: {issues: number; warnings: number; passed: number},
): string => {
    switch (state) {
        case 'critical':
            return `${counts.issues} ${counts.issues === 1 ? 'issue' : 'issues'}`;
        case 'warning':
            return `${counts.warnings} ${counts.warnings === 1 ? 'warning' : 'warnings'}`;
        default:
            return `${counts.passed} passed`;
    }
};

/** Roll-up label for findings that only confirm a check ran. */
export const passingRollUp = (passed: number, skipped: number): string => {
    const checks = `${passed} ${passed === 1 ? 'check' : 'checks'} passed`;
    return skipped > 0 ? `${checks} · ${skipped} skipped` : checks;
};

/**
 * Run-level tally beside the verdict, returned as parts rather than a joined
 * string so the caller can render the separators as padded elements. A bare
 * `·` between two numbers sits tight against them at this size, and no amount
 * of plain spacing in a string fixes that as well as real padding does.
 *
 * Counts that are zero are omitted entirely.
 */
export const resultTallyParts = (counts: {
    issues: number;
    warnings: number;
    passed: number;
    skipped: number;
}): string[] => {
    const parts: string[] = [];
    if (counts.issues > 0)   parts.push(`${counts.issues} ${counts.issues === 1 ? 'issue' : 'issues'}`);
    if (counts.warnings > 0) parts.push(`${counts.warnings} ${counts.warnings === 1 ? 'warning' : 'warnings'}`);
    if (counts.passed > 0)   parts.push(`${counts.passed} passed`);
    if (counts.skipped > 0)  parts.push(`${counts.skipped} skipped`);
    return parts;
};

/**
 * Label for a previous run, which may predate a verdict being recorded. Falls
 * back through the run's lifecycle status so the list speaks the same
 * vocabulary as the rest of the report rather than leaking a raw status slug.
 */
export const runLabel = (verdict?: keyof typeof verdicts, status?: string): string => {
    if (verdict && verdicts[verdict]) return verdicts[verdict].label;
    switch (status) {
        case 'error':   return states.failed;
        case 'running': return states.running;
        case 'queued':  return states.pending;
        default:        return status ?? states.pending;
    }
};

export const accessibility = {
    httpStatus:   'HTTP Status',
    finalUrl:     'Final URL',
    upgraded:     'Upgraded',
    upgradedTo:   'http → https',
    server:       'Server',
    contentType:  'Content-Type',
    size:         'Size',
    cacheControl: 'Cache-Control',
    age:          'Age',
    xCache:       'X-Cache',
    etag:         'ETag',
    expires:      'Expires',
} as const;

export const content = {
    noTitle:            '(no title)',
    noUrl:              '(no URL)',
    noImageLabel:       'No image',
    unknownDimensions:  'unknown dimensions',
    unreachable:        'unreachable',
    ogImage:            'og:image',
    feedMedia:          'feed media',
    chipSkipped:        'Skipped',
    chipPageUnreachable: 'Item page unreachable',
    chipCloudflare:     'Cloudflare challenge',
    chipProxyOnly:      'Proxy-only access',
    chipImageUnreachable: 'og:image unreachable',
} as const;

export const social = {
    noData:      'No destination data.',
    colDestination: 'Destination',
    colOk:       'OK',
    colThrottled: 'Throttled',
    colRejected: 'Rejected',
    colLimit:    'Limit',
    colOther:    'Other',
    legend: [
        {term: 'OK',        definition: 'posts that succeeded.'},
        {term: 'Throttled', definition: 'dlvr.it paused posting to stay within platform safety limits.'},
        {term: 'Rejected',  definition: 'the platform rejected the post.'},
        {term: 'Limit',     definition: 'subscription / capacity limit reached.'},
        {term: 'Other',     definition: 'any other error.'},
    ],
} as const;

export const settings = {
    /* Named for its content rather than repeating the disclosure's own label,
       which sits directly above it. */
    clausesHeading: 'Clauses',
    filterEmpty:    'Filter is empty.',
    clauseJoiner:   'of:',
} as const;

export const previousRuns = {
    colWhen:     'When',
    colVerdict:  'Verdict',
    colDuration: 'Duration',
    noDuration:  '—',
} as const;

/* ------------------------------------------------------- interpolated copy */

export const sampledItems = (sampled: number, total: number): string =>
    `Sampled ${sampled} of ${total} items in the feed.`;

export const postsLookback = (days: number): string =>
    `Number of posts in the last ${days} days, grouped by outcome.`;

export const accountFallback = (paId: number): string => `Account #${paId}`;

export const missingTags = (tags: string[]): string => `Missing: ${tags.join(', ')}`;

export const previousRunsLabel = (count: number): string => `Previous runs (${count})`;

export const filterSummary = (sampled: number, passed: number, failed: number): string =>
    `Sampled ${sampled} feed items: ${passed} pass, ${failed} filtered out.`;

export const imageSummary = (dimensions: string, format: string, kb: number): string =>
    `${dimensions}${format ? ` (${format})` : ''}, ${kb} KB`;

export const progressLabel = (phaseLabel: string, pct: number): string => `${phaseLabel} (${pct}%)`;

export const durationSeconds = (ms: number): string => `${(ms / 1000).toFixed(1)}s`;
